/**
 * Email logo overrides (header / footer).
 * Empty URL → Go var on Apply (unless Apply uploaded store logo PNG);
 * product placeholder on Preview. Height is always `auto`.
 */

import {
    DEFAULT_LOGO_SETTINGS,
    LOGO_GO_SRC,
    PRODUCT_LOGO_PLACEHOLDER,
} from "../constants/logos";

/**
 * @param {string} value
 * @param {string} fallback
 */
function normalizeWidthAttr(value, fallback) {
    const raw = String(value ?? "").trim();
    if (!raw || raw.toLowerCase() === "auto") return fallback;
    const num = raw.match(/^(\d+(\.\d+)?)/);
    return num ? num[1] : fallback;
}

/**
 * Merge partial logo settings with defaults (height always auto — omitted).
 * @param {Partial<typeof DEFAULT_LOGO_SETTINGS> | null | undefined} logoSettings
 */
export function mergeLogoSettings(logoSettings) {
    const merged = { ...DEFAULT_LOGO_SETTINGS, ...(logoSettings || {}) };
    // Drop legacy height keys if present in older state.
    delete merged.headerHeight;
    delete merged.footerHeight;
    return merged;
}

/**
 * Resolve {{THEME.logo*}} tokens.
 * @param {Partial<typeof DEFAULT_LOGO_SETTINGS> | null | undefined} logoSettings
 * @param {{ forPreview?: boolean }} [options]
 */
export function resolveLogoTokens(logoSettings, { forPreview = false } = {}) {
    const s = mergeLogoSettings(logoSettings);

    const headerUrl = String(s.headerUrl || "").trim();
    const footerUrl = String(s.footerUrl || "").trim();

    const headerWidth = normalizeWidthAttr(
        s.headerWidth,
        DEFAULT_LOGO_SETTINGS.headerWidth,
    );
    const footerWidth = normalizeWidthAttr(
        s.footerWidth,
        DEFAULT_LOGO_SETTINGS.footerWidth,
    );

    return {
        logoHeaderSrc: headerUrl
            ? headerUrl
            : forPreview
            ? PRODUCT_LOGO_PLACEHOLDER
            : LOGO_GO_SRC.header,
        logoHeaderWidth: headerWidth,
        logoHeaderMaxWidth: `${headerWidth}px`,
        logoHeaderHeightCss: "auto",
        logoFooterSrc: footerUrl
            ? footerUrl
            : forPreview
            ? PRODUCT_LOGO_PLACEHOLDER
            : LOGO_GO_SRC.footer,
        logoFooterWidth: footerWidth,
        logoFooterMaxWidth: `${footerWidth}px`,
        logoFooterHeightCss: "auto",
    };
}

/**
 * True when logo settings differ from defaults (enables Reset).
 * @param {Partial<typeof DEFAULT_LOGO_SETTINGS> | null | undefined} logoSettings
 */
export function hasLogoOverrides(logoSettings) {
    if (!logoSettings) return false;
    const merged = mergeLogoSettings(logoSettings);
    return Object.keys(DEFAULT_LOGO_SETTINGS).some(
        key => String(merged[key]) !== String(DEFAULT_LOGO_SETTINGS[key]),
    );
}
